import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { UtensilsCrossed } from 'lucide-react'
import { Button } from '../components/ui/Button'
import { TableFloorPlan } from '../components/tables/TableFloorPlan'
import localDatabase from '../services/localDatabase'
import { publishCrossTab, subscribeCrossTab } from '../services/crossTab'
import { pushPendingToSupabase } from '../services/supabaseSync'
import { useTenant } from '../contexts/TenantContext'
import type { Table, Shift, Reservation } from '../types'
import { isWalkInSystemCustomer } from '../lib/customerClassification'
import { getShiftForCurrentTime } from '../lib/shiftUtils'
import { useCoalescedSyncRefresh } from '../hooks/useCoalescedSyncRefresh'

const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || '').split(':').map(Number)
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0)
}

const isTimeWithinShift = (time: string, shift: Shift | undefined): boolean => {
  if (!shift || !time) return false
  const target = toMinutes(time)
  const start = toMinutes(shift.start_time)
  let end = toMinutes(shift.end_time)
  if (shift.end_time === '24:00') end = 24 * 60

  if (end > start) return target >= start && target < end
  return target >= start || target < end
}

const Tables: React.FC = () => {
  const { tenantId, dbReady } = useTenant()
  const [selectedShift, setSelectedShift] = useState('')
  const selectedShiftRef = useRef(selectedShift)
  const refreshSeqRef = useRef(0)
  /** Which refresh generation owns the full-page spinner (avoids stuck loading when a silent refresh supersedes). */
  const spinnerOwnerSeqRef = useRef(0)
  const [tables, setTables] = useState<Table[]>([])
  const [shifts, setShifts] = useState<Shift[]>([])
  const lastKnownShiftsRef = useRef<Shift[]>([])
  const [reservations, setReservations] = useState<Reservation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    selectedShiftRef.current = selectedShift
  }, [selectedShift])

  const refreshData = useCallback(async ({ showSpinner = false }: { showSpinner?: boolean } = {}) => {
    if (!tenantId || !dbReady || !localDatabase.isInitialized()) {
      return
    }

    const seq = ++refreshSeqRef.current

    if (showSpinner) {
      spinnerOwnerSeqRef.current = seq
      setLoading(true)
      setError(null)
    }
    try {
      const today = new Date().toISOString().split('T')[0]
      const [dbTables, dbShifts, dbReservations, dbCustomers] = await Promise.all([
        localDatabase.getAllTables(),
        localDatabase.getAllShifts(),
        localDatabase.getReservationsByDate(today),
        localDatabase.getAllCustomers()
      ])

      if (seq !== refreshSeqRef.current) return

      const normalizeStatus = (status: string | undefined) => {
        if (!status) return 'available'
        if (status === 'active') return 'available'
        if (status === 'inactive') return 'reserved'
        if (status === 'maintenance') return 'reserved'
        return status as any
      }

      setTables(
        (dbTables as any[]).map(table => ({
          ...table,
          status: normalizeStatus(table.status)
        })) as Table[]
      )

      const normalizedShifts = (dbShifts as any[]).map((shift) => ({
        ...shift,
        id: String(shift.id),
      })) as Shift[]
      const availableShifts = normalizedShifts.length
        ? normalizedShifts
        : lastKnownShiftsRef.current
      if (normalizedShifts.length) {
        lastKnownShiftsRef.current = normalizedShifts
      }
      setShifts(availableShifts)

      // Hydrate reservations with customer data and normalize time
      const hydratedReservations = (dbReservations as any[]).map(r => ({
        ...r,
        time: r.time ? r.time.substring(0, 5) : r.time,
        customer: (dbCustomers as any[]).find(c => c.id === r.customer_id)
      }));

      // Auto-clean invalid orphan walk-ins:
      // walk-in/system customer + no table + outside its shift window.
      const shiftsById = new Map(availableShifts.map((s) => [String(s.id), s]))
      const invalidWalkIns = availableShifts.length > 0
        ? hydratedReservations.filter((r: any) => {
            const shift = shiftsById.get(String(r.shift_id))
            const isWalkIn = isWalkInSystemCustomer(r.customer as any) || String(r.notes || '').toLowerCase() === 'walk-in'
            return isWalkIn && !r.table_id && !isTimeWithinShift(r.time, shift)
          })
        : []
      if (invalidWalkIns.length > 0) {
        for (const r of invalidWalkIns) {
          await localDatabase.deleteReservation(String(r.id))
        }
        if (seq !== refreshSeqRef.current) return
        publishCrossTab({ type: 'sync:reservations' })
        if (navigator.onLine) {
          pushPendingToSupabase().catch(err => console.warn('Push pending failed (cleanup walk-ins):', err))
        }
      }

      const cleanedReservations = hydratedReservations.filter(
        (r: any) => !invalidWalkIns.some((bad: any) => String(bad.id) === String(r.id))
      )
      setReservations(cleanedReservations as unknown as Reservation[])

      // Determine selected shift for this load:
      // 1) If current selection is still valid, keep it for this visit.
      // 2) Otherwise, auto-detect based on current time.
      // 3) Fallback to the first available shift.
      const currentSel = selectedShiftRef.current
      if (!currentSel || !availableShifts.some(shift => String(shift.id) === String(currentSel))) {
        let nextSelected: string | undefined

        const autoShiftId = getShiftForCurrentTime(availableShifts)
        if (autoShiftId && availableShifts.some(shift => String(shift.id) === String(autoShiftId))) {
          nextSelected = String(autoShiftId)
        } else if (availableShifts.length > 0) {
          nextSelected = String(availableShifts[0].id)
        }

        if (nextSelected) {
          setSelectedShift(String(nextSelected))
        } else {
          setSelectedShift('')
        }
      }
    } catch (err) {
      if (seq !== refreshSeqRef.current) return
      console.error('Error fetching table data:', err)
      setError(err instanceof Error ? err.message : 'Αποτυχία φόρτωσης τραπεζιών.')
      setTables([])
      setReservations([])
    } finally {
      if (showSpinner && spinnerOwnerSeqRef.current === seq) {
        setLoading(false)
      }
    }
  }, [tenantId, dbReady])

  const scheduleCrossTabRefresh = useCoalescedSyncRefresh(() => {
    void refreshData()
  })

  useEffect(() => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    refreshData({ showSpinner: true })
  }, [refreshData, tenantId, dbReady])

  useEffect(() => {
    if (!tenantId || !dbReady) {
      return; // Wait for tenant database to be ready
    }
    
    const unsubscribe = subscribeCrossTab((message) => {
      if (
        message.type === 'sync:reservations' ||
        message.type === 'sync:tables' ||
        message.type === 'sync:shifts' ||
        message.type === 'sync:all'
      ) {
        scheduleCrossTabRefresh()
      }
    })
    return () => {
      unsubscribe()
    }
  }, [scheduleCrossTabRefresh, tenantId, dbReady])

  // Keep full same-day reservation set so table occupancy can carry across shifts
  // (e.g. 17:00 booking still occupying at 18:00).
  const reservationsForShift = useMemo(() => reservations, [reservations])

  const handleTableUpdate = async (tableId: string, updates: Partial<Table>) => {
    try {
      await localDatabase.updateTable(tableId, updates as any)
      await refreshData()
      publishCrossTab({ type: 'sync:tables' })
      if (navigator.onLine) {
        await pushPendingToSupabase()
      }
    } catch (err) {
      console.error('Table update failed:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Φόρτωση τραπεζιών...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Σφάλμα φόρτωσης</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Δοκιμή ξανά
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center space-x-3">
          <UtensilsCrossed className="w-6 h-6 md:w-8 md:h-8 text-purple-500 flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-xl md:text-3xl font-bold text-foreground truncate">Τραπέζια</h1>
            <p className="text-sm md:text-base text-muted-foreground hidden md:block">Προβολή και διαχείριση κατάστασης τραπεζιών</p>
          </div>
        </div>
      </div>
      
      <TableFloorPlan
        tables={tables}
        shifts={shifts}
        reservations={reservationsForShift}
        selectedShift={selectedShift}
        onShiftChange={setSelectedShift}
        onTableUpdate={handleTableUpdate}
      />
    </div>
  )
}

export default Tables 
